import { useEffect, useRef } from "react";
import maplibregl, { Map } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

const MAPTILER_KEY = import.meta.env.VITE_MAPTILER_KEY as string;
const STYLE_URL = `https://api.maptiler.com/maps/streets-v4/style.json?key=${MAPTILER_KEY}`;
const EMPTY: GeoJSON.FeatureCollection = {
  type: "FeatureCollection",
  features: [],
};

export function JobTrackMap({ geo }: { geo: GeoJSON.FeatureCollection }) {
  const mapRef = useRef<Map | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const latestGeoRef = useRef<GeoJSON.FeatureCollection>(geo);

  latestGeoRef.current = geo;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STYLE_URL,
      center: [6.95, 50.94],
      zoom: 12,
    });

    map.addControl(new maplibregl.NavigationControl(), "top-right");
    mapRef.current = map;

    map.once("load", () => {
      map.addSource("job-track", { type: "geojson", data: EMPTY });

      map.addLayer({
        id: "route-line",
        type: "line",
        source: "job-track",
        filter: ["==", ["get", "kind"], "route"],
        paint: { "line-width": 4 },
      });

      map.addLayer({
        id: "fix-points",
        type: "circle",
        source: "job-track",
        filter: ["==", ["get", "kind"], "fix"],
        paint: { "circle-radius": 4, "circle-stroke-width": 1 },
      });

      // apply latest geo after source exists
      const src = map.getSource("job-track") as any;
      src?.setData?.(latestGeoRef.current);
      fitToData(map, latestGeoRef.current);

      // if the container was initially sized weirdly, this helps
      map.resize();
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const src = map.getSource("job-track") as any;
    if (src?.setData) {
      src.setData(geo);
      fitToData(map, geo);
    } else {
      // source not ready yet; update will be applied in "load" via latestGeoRef
    }
  }, [geo]);

  return <div ref={containerRef} style={{ height: "100%", width: "100%" }} />;
}

function fitToData(map: Map, geo: GeoJSON.FeatureCollection) {
  // Prefer route LineString, fall back to all points
  const coords: [number, number][] = [];

  for (const f of geo.features ?? []) {
    const g: any = f.geometry;
    if (!g) continue;

    if (g.type === "LineString") {
      for (const c of g.coordinates ?? []) coords.push(c);
    } else if (g.type === "Point") {
      coords.push(g.coordinates);
    }
  }

  if (coords.length === 0) return;

  let minLng = coords[0][0],
    maxLng = coords[0][0];
  let minLat = coords[0][1],
    maxLat = coords[0][1];

  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }

  map.fitBounds(
    [
      [minLng, minLat],
      [maxLng, maxLat],
    ],
    { padding: 40, duration: 0 },
  );
}
